import { styled } from 'styled-components';

import { Box } from '@hivebrite/design-system/Box';

import { AI_FORM_CONFIG } from 'hb-react/shared/components/AIFormWidget/constants';

interface FloatingTriggerProps {
  offset: { x: number; y: number };
  onClick: () => void;
  position: 'bottom-left' | 'bottom-right' | 'top-left' | 'top-right';
}

export const FloatingTrigger = ({
  onClick,
  position,
  offset,
}: FloatingTriggerProps) => (
  <FloatingActionButton
    position={position}
    offset={offset}
    onClick={onClick}
    aria-label="Open the sidepanel"
  >
    <Box fontSize="24px" lineHeight={1}>
      🤖
    </Box>
  </FloatingActionButton>
);

const FloatingActionButton = styled.button<{
  offset: { x: number; y: number };
  position: string;
}>`
  background: ${({ theme }) =>
    `linear-gradient(135deg, ${theme.colors.primary} 0%, #8b5cf6 100%)`};
  position: fixed;
  border: none;
  outline: none;
  padding: 0;
  margin: 0;

  ${({ position, offset }) => {
    const isBottom = position.includes('bottom');
    const isRight = position.includes('right');

    return `
      ${isBottom ? 'bottom' : 'top'}: ${offset.y}px;
      ${isRight ? 'right' : 'left'}: ${offset.x}px;
    `;
  }}

  width: ${AI_FORM_CONFIG.WIDGET_SIZE}px;
  height: ${AI_FORM_CONFIG.WIDGET_SIZE}px;
  border-radius: 50%;
  cursor: pointer;
  z-index: ${({ theme }) => theme.zIndices.positioned};
  display: flex;
  align-items: center;
  justify-content: center;

  /* Multi-layered shadow with strong visibility */
  box-shadow:
    0 6px 20px rgba(0, 0, 0, 0.15),
    0 4px 16px rgba(0, 0, 0, 0.12),
    0 0 0 2px rgba(255, 255, 255, 0.1),
    0 0 25px rgba(59, 130, 246, 0.2),
    0 0 50px rgba(139, 92, 246, 0.15);

  /* Gradient border effect */
  border: 2px solid transparent;
  background-clip: padding-box;

  /* Enhanced gradient background with animated border */
  &::before {
    content: '';
    position: absolute;
    top: -2px;
    left: -2px;
    right: -2px;
    bottom: -2px;
    background: linear-gradient(45deg, #3b82f6, #8b5cf6, #06b6d4, #3b82f6);
    background-size: 300% 300%;
    border-radius: 50%;
    z-index: -1;
    animation: ai-border-pulse 3s ease-in-out infinite;
  }

  /* Pulsing glow animation */
  animation: ai-pulse 2s ease-in-out infinite alternate;

  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);

  &:hover {
    transform: scale(1.1) translateY(-3px);
    animation: ai-pulse-fast 1s ease-in-out infinite alternate;
    box-shadow:
      0 12px 35px rgba(0, 0, 0, 0.2),
      0 8px 25px rgba(0, 0, 0, 0.15),
      0 0 0 3px rgba(255, 255, 255, 0.15),
      0 0 40px rgba(59, 130, 246, 0.3),
      0 0 70px rgba(139, 92, 246, 0.25);
  }

  &:active {
    transform: scale(1.05) translateY(-1px);
  }

  /* Pulsing glow keyframes */
  @keyframes ai-pulse {
    0% {
      box-shadow:
        0 6px 20px rgba(0, 0, 0, 0.15),
        0 4px 16px rgba(0, 0, 0, 0.12),
        0 0 0 2px rgba(255, 255, 255, 0.1),
        0 0 25px rgba(59, 130, 246, 0.2),
        0 0 50px rgba(139, 92, 246, 0.15);
    }
    100% {
      box-shadow:
        0 8px 25px rgba(0, 0, 0, 0.18),
        0 6px 20px rgba(0, 0, 0, 0.15),
        0 0 0 2px rgba(255, 255, 255, 0.15),
        0 0 35px rgba(59, 130, 246, 0.25),
        0 0 60px rgba(139, 92, 246, 0.2);
    }
  }

  @keyframes ai-pulse-fast {
    0% {
      box-shadow:
        0 12px 35px rgba(0, 0, 0, 0.2),
        0 8px 25px rgba(0, 0, 0, 0.15),
        0 0 0 3px rgba(255, 255, 255, 0.15),
        0 0 40px rgba(59, 130, 246, 0.3),
        0 0 70px rgba(139, 92, 246, 0.25);
    }
    100% {
      box-shadow:
        0 12px 35px rgba(0, 0, 0, 0.2),
        0 8px 25px rgba(0, 0, 0, 0.15),
        0 0 0 3px rgba(255, 255, 255, 0.2),
        0 0 50px rgba(59, 130, 246, 0.35),
        0 0 80px rgba(139, 92, 246, 0.3);
    }
  }

  @keyframes ai-border-pulse {
    0% {
      background-position: 0% 50%;
    }
    50% {
      background-position: 100% 50%;
    }
    100% {
      background-position: 0% 50%;
    }
  }
`;
